import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit,
  Category,
  Description,
  DirectionsCar,
  ExpandMore,
  ExpandLess,
  DataObject,
  CalendarToday,
  Source,
} from '@mui/icons-material';
import PercentIcon from '@mui/icons-material/Percent';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import PriceCodeDisplay from '../../../controlPanel/components/PriceCodeDisplay.tsx';
import { Product } from '../../types';
import { usePermissions } from '../../../../utils/usePermissions';
import { baseUrlImage } from '../../../../utils/apiClient';
import { DEMO_IMAGE_URL } from '../ProductsList';

const ProductDetails: React.FC = () => {
  const { canUpdateInventory } = usePermissions();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    compatibility: true,
    attributes: true,
    source: true,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await request({
          url: `product/get-one-product/${id}`,
          method: 'get',
        });

        if (res.status === 'success' && res.product) {
          setProduct(res.product);
        } else {
          toast.error('Product not found');
          navigate('/inventory');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/inventory');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, navigate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'error';
  };

  const getStockStatus = (quantity: number) => {
    if (quantity === 0) return { text: 'Out of Stock', color: 'error' as const };
    if (quantity < 10) return { text: 'Low Stock', color: 'warning' as const };
    return { text: 'In Stock', color: 'success' as const };
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Responsive layout helper
  const renderTwoColumnLayout = (leftContent: React.ReactNode, rightContent: React.ReactNode) => {
    if (isMobile) {
      return (
        <Stack spacing={2}>
          {leftContent}
          {rightContent}
        </Stack>
      );
    }

    return (
      <Box display="flex" gap={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {leftContent}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {rightContent}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading product details...</Typography>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Product not found</Typography>
      </Box>
    );
  }

  const stockStatus = getStockStatus(product.quantity);
  const profitMargin = product.sellingPriceB2C - product.purchasePrice;
  const profitPercentage = ((profitMargin / product.purchasePrice) * 100).toFixed(1);

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Brandcrumb />
        <Chip
          onClick={() => navigate(-1)}
          icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
          label="Back"
          variant="filled"
          color="secondary"
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>

      {/* Compact Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" gap={1.5} alignItems="flex-start">
            <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50, flexShrink: 0 }}>
              <InventoryIcon sx={{ fontSize: 24 }} />
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight={600}
                  noWrap
                  sx={{
                    flex: 1,
                    fontSize: isMobile ? '1.1rem' : '1.25rem'
                  }}
                >
                  {product.name}
                </Typography>
                {canUpdateInventory() && (<Tooltip title="Edit Product">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/inventory/editProduct/${product._id}`)}
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    <Edit fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>)}

              </Box>

              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  label={product.status}
                  size="small"
                  color={getStatusColor(product.status)}
                />
                <Chip
                  label={stockStatus.text}
                  size="small"
                  color={stockStatus.color}
                  variant="outlined"
                />
                {product.source && (
                  <Chip
                    label={`Source: ${product.source.type}`}
                    size="small"
                    color={
                      product.source.type === 'manual' ? 'default' :
                        product.source.type === 'price-list' ? 'primary' :
                          product.source.type === 'import' ? 'secondary' : 'warning'
                    }
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                )}
              </Box>

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Part No:</strong> {product.partNo}
                </Typography>
                {product.barcode && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Barcode:</strong> {product.barcode}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Content - Two Column Layout */}
      {renderTwoColumnLayout(
        /* Left Column - Images */
        <Card>
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            {/* Main Image */}
            <Box
              sx={{
                width: '100%',
                height: isMobile ? 200 : 250,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 1,
                mb: 1,
                overflow: 'hidden',
              }}
            >
              <img
                src={product.productImages?.[selectedImage] ? (baseUrlImage + product.productImages?.[selectedImage]) : DEMO_IMAGE_URL}
                alt={product.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Thumbnail Images */}
            {product.productImages && product.productImages.length > 1 && (
              <Box display="flex" gap={0.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
                {product.productImages.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 50,
                      height: 50,
                      border: selectedImage === index ? 2 : 1,
                      borderColor: selectedImage === index ? 'primary.main' : 'divider',
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={baseUrlImage + image}
                      alt={`${product.name} ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>,

        /* Right Column - Product Details */
        <Stack spacing={1.5}>
          {/* Pricing Card */}
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h6" fontWeight={600} color="primary" gutterBottom sx={{ fontSize: '1.25rem' }}>
                {formatCurrency(product.sellingPriceB2C)}
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    MRP
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(product.mrp)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Unit Price
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(product.unitPrice) }
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Purchase
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    <PriceCodeDisplay number={product.purchasePrice} />
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    B2B Price
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(product.sellingPriceB2B)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Profit
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={profitMargin > 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(profitMargin)} ({profitPercentage}%)
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ height: 1, backgroundColor: 'divider', my: 1 }} />

              {/* Basic Info */}
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.brand}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.category}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendor
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.vender}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Stock
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={stockStatus.color}
                  >
                    {product.quantity} units
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Purchase Details Card */}
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Purchase Details
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Purchase Discount
                  </Typography>
                  <Typography variant="body2" fontWeight={500} display="flex" alignItems="center">
                    {product.purchaseDiscount}<PercentIcon sx={{ fontSize: "14px", ml: 0.25 }} />
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Margin
                  </Typography>
                  <Typography variant="body2" fontWeight={500} color={profitMargin > 0 ? 'success.main' : 'error.main'}>
                    {profitPercentage}%
                  </Typography>
                </Box>

                {product.importBatchId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Import Batch
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                      {product.importBatchId.substring(0, 8)}...
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Details Sections */}
      <Stack spacing={1.5} sx={{ mt: 2 }}>

        {/* Description Section */}
        <Card>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              borderBottom: expandedSections.description ? 1 : 0,
              borderColor: 'divider',
            }}
            onClick={() => toggleSection('description')}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Description color="primary" sx={{ fontSize: 18 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Description
              </Typography>
              {product.description?.jsonFields && Object.keys(product.description.jsonFields).length > 0 && (
                <Chip
                  label={`+${Object.keys(product.description.jsonFields).length}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "secondary.contrastText",
                    borderColor: "secondary.contrastText"
                  }}
                />
              )}
            </Box>
            <IconButton size="small">
              {expandedSections.description ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={expandedSections.description}>
            <Box sx={{ p: 1.5 }}>
              {product.description?.text ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, mb: 2 }}>
                  {product.description.text}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" mb={2}>
                  No description available.
                </Typography>
              )}

              {product.description?.jsonFields && Object.keys(product.description.jsonFields).length > 0 && (
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom>
                    Additional Information
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                    {Object.entries(product.description.jsonFields).map(([key, value]) => {
                      const displayValue = typeof value === 'string' ? value : JSON.stringify(value);
                      return (
                        <Chip
                          key={key}
                          label={`${key}: ${displayValue}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            maxWidth: '150px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </Card>

        {/* Compatibility Section */}
        <Card>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              borderBottom: expandedSections.compatibility ? 1 : 0,
              borderColor: 'divider',
            }}
            onClick={() => toggleSection('compatibility')}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <DirectionsCar color="primary" sx={{ fontSize: 18 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Compatibility
              </Typography>
              <Chip
                label={product.compatibility?.length || 0}
                size="small"
                variant="outlined"
                sx={{
                  color: "secondary.contrastText",
                  borderColor: "secondary.contrastText"
                }}
              />
            </Box>
            <IconButton size="small">
              {expandedSections.compatibility ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={expandedSections.compatibility}>
            <Box sx={{ p: 1.5 }}>
              {product.compatibility && product.compatibility.length > 0 ? (
                <Box display="flex" flexDirection={isMobile ? "column" : "row"} flexWrap="wrap" gap={1}>
                  {product.compatibility.map((comp, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        flex: isMobile ? 1 : `0 0 calc(50% - 8px)`,
                        minWidth: 0
                      }}
                    >
                      {comp.baseImage && (
                        <Avatar
                          variant="rounded"
                          src={(baseUrlImage + comp.baseImage)}
                          sx={{ width: 50, height: 38 }}
                        />
                      )}
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary">
                        {comp.brand.name} {comp.name}
                      </Typography>

                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {comp.variants.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Variants
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.25}>
                              {comp.variants.slice(0, 3).map((variant, i) => (
                                <Chip key={i} label={variant} size="small" variant="outlined" />
                              ))}
                              {comp.variants.length > 3 && (
                                <Chip label={`+${comp.variants.length - 3}`} size="small" />
                              )}
                            </Box>
                          </Box>
                        )}

                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {comp.fuelTypes.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Fuel
                              </Typography>
                              <Typography variant="caption" fontWeight={500}>
                                {comp.fuelTypes.join(', ')}
                              </Typography>
                            </Box>
                          )}

                          {comp.transmissions.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Transmission
                              </Typography>
                              <Typography variant="caption" fontWeight={500}>
                                {comp.transmissions.join(', ')}
                              </Typography>
                            </Box>
                          )}

                          {comp.generations.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Generations
                              </Typography>
                              <Typography variant="caption" fontWeight={500}>
                                {comp.generations[0].from}-{comp.generations[0].to}
                                {comp.generations.length > 1 && ` (+${comp.generations.length - 1})`}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No compatibility information available.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Card>

        {/* Attributes Section */}
        <Card>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              borderBottom: expandedSections.attributes ? 1 : 0,
              borderColor: 'divider',
            }}
            onClick={() => toggleSection('attributes')}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Category color="primary" sx={{ fontSize: 18 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Attributes
              </Typography>
              <Chip
                label={Object.keys(product.attributes || {}).length}
                size="small"
                variant="outlined"
                sx={{
                  color: "secondary.contrastText",
                  borderColor: "secondary.contrastText"
                }}
              />
            </Box>
            <IconButton size="small">
              {expandedSections.attributes ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={expandedSections.attributes}>
            <Box sx={{ p: 1.5 }}>
              {product.attributes && Object.keys(product.attributes).length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(product.attributes).map(([key, value]) => (
                    <Paper
                      key={key}
                      variant="outlined"
                      sx={{
                        p: 1,
                        flex: `0 0 calc(50% - 8px)`,
                        minWidth: 0
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {key}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {value?.toString() || 'Not specified'}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No additional attributes available.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Card>

        {/* Source & Import Information Section */}
        {product.source && (
          <Card>
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderBottom: expandedSections.source ? 1 : 0,
                borderColor: 'divider',
              }}
              onClick={() => toggleSection('source')}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Source color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Source & Import
                </Typography>
                {product.importBatchId && (
                  <Chip
                    label="Imported"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
              <IconButton size="small">
                {expandedSections.source ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={expandedSections.source}>
              <Box sx={{ p: 1.5 }}>
                <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                  <Paper variant="outlined" sx={{ p: 1, flex: '1 1 150px', minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <DataObject color="primary" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        Source Type
                      </Typography>
                    </Box>
                    <Chip
                      label={product.source.type}
                      size="small"
                      color={
                        product.source.type === 'manual' ? 'default' :
                          product.source.type === 'price-list' ? 'primary' :
                            product.source.type === 'import' ? 'secondary' : 'warning'
                      }
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Paper>

                  {product.source.id && (
                    <Paper variant="outlined" sx={{ p: 1, flex: '1 1 150px', minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <InventoryIcon color="primary" sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Source ID
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {product.source.id}
                      </Typography>
                    </Paper>
                  )}

                  {product.source.date && (
                    <Paper variant="outlined" sx={{ p: 1, flex: '1 1 150px', minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <CalendarToday color="primary" sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Date
                        </Typography>
                      </Box>
                      <Typography variant="caption" fontWeight={500}>
                        {new Date(product.source.date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Paper>
                  )}
                </Box>

                {product.source.metadata && Object.keys(product.source.metadata).length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom>
                      Source Metadata
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                      {Object.entries(product.source.metadata).map(([key, value]) => {
                        const displayValue = typeof value === 'string' ? value : JSON.stringify(value);
                        return (
                          <Chip
                            key={key}
                            label={`${key}: ${displayValue}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              maxWidth: '150px',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Collapse>
          </Card>
        )}
      </Stack>
    </>
  );
};

export default ProductDetails;